import { useEffect } from 'react';

import {
  Button,
  Editor,
  FormFeedback,
  FormGroup,
  FormLabel,
  FormRow,
  FormSection,
  Input,
  SelectDropdown,
} from '@opositandopn/ui';
import { useFormikContext } from 'formik';

import { DeleteStepButton } from '../../../../../components/shared/admin/layout/DeleteStepButton';
import { StepperBody } from '../../../../../components/shared/admin/layout/StepperBody';
import { StepperFooter } from '../../../../../components/shared/admin/layout/StepperFooter';
import { editorLabels } from '../../../../../components/shared/editor/EditorLabels';
import { BaseComponentProps } from '../../../../../types/base-component.types';
import { DifficultyLevel } from '../../../../questions/api/useMultipleChoiceQuestions';
import { TrainingPlanFormValues } from '../TrainingPlanEditor';

import styles from './TrainingPlanStep.module.scss';

type ContainerProps = BaseComponentProps & {
  onNext: () => void;
  onCancel: () => void;
  isDeleting: boolean;
  onDelete: () => void;
};

function TrainingPlanStep({
  onNext,
  onCancel,
  isDeleting,
  onDelete,
}: ContainerProps) {
  const { setTouched, values, touched, errors, isSubmitting, handleChange } =
    useFormikContext<TrainingPlanFormValues>();

  useEffect(() => {
    if (values.id) {
      setTouched(
        Object.keys(values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      );
    }
  }, [values]);

  return (
    <>
      <StepperBody>
        <FormSection
          title={values.id ? 'Editar plan' : 'Nuevo plan de entrenamiento'}
        >
          <FormRow>
            <FormGroup>
              <FormLabel>Nombre</FormLabel>
              <Input
                id="name"
                value={values.name}
                onChange={handleChange}
                error={touched.name && !!errors.name}
              />
              {touched.name && errors.name && (
                <FormFeedback variant="danger">{errors.name}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <FormLabel>Dificultad</FormLabel>
              <SelectDropdown
                placeholder="Selecciona una dificultad..."
                onSelect={(value: string | number) => {
                  handleChange({
                    target: {
                      name: 'difficultyLevel',
                      value: value as DifficultyLevel,
                    },
                  });
                }}
                options={[
                  {
                    value: 'easy',
                    label: 'Fácil',
                    selected: values.difficultyLevel === 'easy',
                  },
                  {
                    value: 'medium',
                    label: 'Media',
                    selected: values.difficultyLevel === 'medium',
                  },
                  {
                    value: 'hard',
                    label: 'Difícil',
                    selected: values.difficultyLevel === 'hard',
                  },
                ]}
                error={touched.difficultyLevel && !!errors.difficultyLevel}
              />
              {touched.difficultyLevel && errors.difficultyLevel && (
                <FormFeedback variant="danger">
                  {errors.difficultyLevel}
                </FormFeedback>
              )}
            </FormGroup>
          </FormRow>
          <FormGroup>
            <FormLabel>Descripción</FormLabel>
            <Editor
              placeholder="Escribe aquí la descripción del plan de entrenamiento..."
              labels={editorLabels}
              value={values.description}
              onChange={(value: string | number) => {
                handleChange({
                  target: {
                    name: 'description',
                    value,
                  },
                });
              }}
              error={touched.description && !!errors.description}
            />
            {touched.description && errors.description && (
              <FormFeedback variant="danger">{errors.description}</FormFeedback>
            )}
            <FormFeedback>
              Describe el plan de manera clara y concisa, para que los usuarios
              puedan entender los objetivos y la estructura del entrenamiento.
            </FormFeedback>
          </FormGroup>
        </FormSection>
      </StepperBody>

      <StepperFooter className={styles['footer']}>
        <div className={styles['footer__left']}>
          {values.id && (
            <DeleteStepButton
              title="¿Estás segurx de que deseas eliminar este plan?"
              disabled={isSubmitting}
              isDeleting={isDeleting}
              onDelete={onDelete}
            />
          )}
        </div>
        <div className={styles['footer__right']}>
          <Button
            type="button"
            variant="outline-danger"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="outline-primary"
            disabled={isSubmitting}
            onClick={() => {
              setTouched(
                Object.keys(values).reduce(
                  (acc, key) => ({ ...acc, [key]: true }),
                  {},
                ),
              );

              if (Object.keys(errors).length === 0) {
                onNext();
              }
            }}
          >
            Siguiente
          </Button>
        </div>
      </StepperFooter>
    </>
  );
}

export { TrainingPlanStep };
